import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Entitlement, EntitlementTenant} from '@app/model/entitlement.model';
import {DcpKymaService} from '@app/services/dcp-kyma.service';
import {DcpOrderEntryService} from '@app/services/dcp-order-entry.service';
import {DcpUserService} from '@app/services/user/dcp-user.service';
import {ConfiguratorUriParameterService, DcpGlobalMessageService, HttpRequestOptions} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {OrderEntry} from '@spartacus/cart/base/root/models/cart.model';
import {User} from '@spartacus/core';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {BehaviorSubject, combineLatest, Observable, of} from 'rxjs';
import {catchError, filter, map, switchMap} from 'rxjs/operators';

export interface DcpTenantKey {
  key: string;
  columnLabel: string;
}

export interface DcpTenant {
  tenantId: string;
  customerTenantId: string;
  customerTenantName: string;
  tenantCategoryDescription: string;
}

export interface DcpGlobalAccount {
  globalAccountId: string;
  entitlementSetId: string;
  paymentModel: string;
  paymentModelText: string;
  systemName: string;
  creationDate: string;
}

export interface DcpTenantResponse {
  tenants: DcpTenant[];
  uid: string;
  supplierAlternativeAID: string;
  globalAccounts: DcpGlobalAccount[];
}

export interface DcpTenantsList {
  [key: string]: DcpTenant[];
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DcpEntitlementService {
  private lastOrgUnitUid = '';
  private lastAlternativeSupplierAIDs = '';
  private orderEntriesList$ = new BehaviorSubject<OrderEntry[]>([]);
  private entitlementPath = '/entitlements';
  private tenantSubject = new BehaviorSubject<DcpTenantsList>(undefined);
  private entitlementServiceCallError = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private kymaService: DcpKymaService,
    private uriParamService: ConfiguratorUriParameterService,
    private orderEntryService: DcpOrderEntryService,
    private userFacade: UserAccountFacade,
    private dcpGlobalMessageService: DcpGlobalMessageService,
    private userService: DcpUserService,
  ) {
  }

  public static getTenantKeys(): DcpTenantKey[] {
    return [
      {key: 'tenantId', columnLabel: 'configurator.deploymentSystems.columnSystemNumber'},
      {key: 'customerTenantName', columnLabel: 'configurator.deploymentSystems.columnName'},
      {key: 'tenantCategoryDescription', columnLabel: 'configurator.deploymentSystems.columnRole'},
    ];
  }

  public setupTenantSubscription(): void {
    const user$: Observable<User> = this.userFacade.get();
    const orderEntries$: Observable<OrderEntry[]> = this.orderEntriesList$.asObservable().pipe(filter(entries => !!entries.length));
    combineLatest(
      [
        user$,
        orderEntries$,
      ],
    ).pipe(untilDestroyed(this)).subscribe(([user, orderEntries]) => {
      if (user?.orgUnit?.uid && this.userService.isCustomerReplicatedInCrm(user)) {
        const allEditionAlternativeSupplierAIDs = JSON.stringify(orderEntries?.map((entry) => entry.product?.supplierAlternativeAID));
        if (this.lastOrgUnitUid !== user.orgUnit.uid || this.lastAlternativeSupplierAIDs !== allEditionAlternativeSupplierAIDs) {
          this.lastOrgUnitUid = user.orgUnit.uid;
          this.lastAlternativeSupplierAIDs = allEditionAlternativeSupplierAIDs;
          const allEntitlementCalls: Observable<DcpTenantResponse>[] = this.collectAllEntitlementCalls(orderEntries, user);
          combineLatest(allEntitlementCalls).pipe(untilDestroyed(this)).subscribe(tenantResponses => {
            const tenantList: DcpTenantsList = {};
            tenantResponses.forEach(tenantResponse => {
              tenantList[tenantResponse.supplierAlternativeAID] = tenantResponse.tenants;
            });
            this.tenantSubject.next(tenantList);
          });
        }
      } else {
        this.lastOrgUnitUid = '';
        this.lastAlternativeSupplierAIDs = '';
        this.tenantSubject.next(undefined);
        this.orderEntriesList$.next([]);
      }
    });
  }

  public getTenants(): Observable<DcpTenantsList> {
    return this.tenantSubject.asObservable();
  }

  public getEntitlementCall(uid: string, orderEntry: OrderEntry): Observable<DcpTenantResponse> {
    if (uid) { // the user id is a mandatory parameter for this request
      const entitlementServiceUrl = this.kymaService.getUrl('entitlement');
      return this.getOptions(uid, orderEntry).pipe(
        switchMap(options => this.http.get<any>(`${entitlementServiceUrl}${this.entitlementPath}`, options)),
        catchError((error) => {
          this.dcpGlobalMessageService.addMessage(error, this?.constructor?.name, 'errorLogging');
          if (error?.status !== 404) {
            this.entitlementServiceCallError.next(true);
          }
          return of(error);
        }),
        map(({entitlements, globalAccounts}) => ({
          tenants: this.collectTenants(entitlements),
          uid,
          supplierAlternativeAID: orderEntry?.product.supplierAlternativeAID,
          globalAccounts: this.collectGlobalAccounts(globalAccounts)
        } as DcpTenantResponse)),
      );
    }
    return of(null);
  }

  public getEntitlementServiceCallErrorStatus(): Observable<boolean> {
    return this.entitlementServiceCallError.asObservable();
  }

  public getEntitlement(orderEntry: OrderEntry): Observable<{
    globalAccounts;
    tenants;
  }> {
    const user$: Observable<User> = this.userFacade.get();
    return user$.pipe(
      filter(user => this.userService.isCustomerReplicatedInCrm(user)),
      filter(user => Boolean(user?.orgUnit?.uid)),
      map(({orgUnit}) => orgUnit.uid),
      switchMap(uid => this.getEntitlementCall(uid, orderEntry)),
      map(d => ({globalAccounts: d?.globalAccounts, tenants: d?.tenants}))
    );
  }

  /**
   * This method adds new orderEntry to list only after
   * verification that this entry is not already added to the list
   * by checking uniq supplierAlternativeAID
   **/

  public updateOrderEntriesList(orderEntry: OrderEntry): void {
    const orderEntriesList = this.orderEntriesList$.getValue();
    if (!orderEntriesList.some((entry) => entry.product.supplierAlternativeAID === orderEntry.product.supplierAlternativeAID)) {
      this.orderEntriesList$.next([...orderEntriesList, orderEntry]);
    }
  }

  private getOptions(uid: string, orderEntry: OrderEntry): Observable<HttpRequestOptions> {
    return this.kymaService.prepareAuthConfig().pipe(
      map(({headers}) => {
        let params = new HttpParams()
          .set('customerId', uid);
        params = params.set('contract_item_id', this.getContractItemId(orderEntry));
        return {headers, params};
      })
    );
  }

  private getContractItemId(orderEntry: OrderEntry): string | null {
    return this.orderEntryService.getConfiguration(orderEntry)?.itemId || this.uriParamService.getItemId();
  }

  private collectAllEntitlementCalls(allEditionEntries: OrderEntry[], user: User): Observable<DcpTenantResponse>[] {
    const allEntitlementCalls: Observable<DcpTenantResponse>[] = [];
    allEditionEntries.forEach((entry) => {
      allEntitlementCalls.push(this.getEntitlementCall(user.orgUnit.uid, entry));
    });
    return allEntitlementCalls;
  }

  private collectTenants(entitlements: [Entitlement]): EntitlementTenant[] | null {
    let tenants: EntitlementTenant[] = null;
    if (entitlements) {
      // flatMap is not available in es2016
      tenants = entitlements.reduce(
        (tenants, entitlement) => tenants.concat(entitlement.tenants), []
      );
    }
    return tenants;
  }

  private collectGlobalAccounts(accounts: [DcpGlobalAccount]): DcpGlobalAccount[] | null {
    let globalAccounts: DcpGlobalAccount[] = null;
    if (accounts) {
      globalAccounts = accounts;
    }
    return globalAccounts;
  }
}
